import React, { useState } from "react";
import { Button, Input, PopupModal, SVG } from "../../shared";
import { Field, Formik, Form } from "formik";
import { SVG_ENUM, VALIDATION_ENUM } from "../../enums";
import * as Yup from "yup";
import { useChangePassword } from "../../hooks/auth";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { mutate: changePassword } = useChangePassword({
    onSuccess: () => {
      onClose();
      setShowError(false);
      setErrorMessage("");
    },
    onError: (error: Error) => {
      setShowError(true);
      setErrorMessage("Błąd podczas zmiany hasła");
    },
  });

  const validation = () =>
    Yup.object().shape({
      currentPassword: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(8, VALIDATION_ENUM.MIN_8),
      newPassword: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(8, VALIDATION_ENUM.MIN_8),
      confirmPassword: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .oneOf([Yup.ref("newPassword")], "Hasła muszą być identyczne"),
    });

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const onSubmit = (values: typeof initialValues) => {
    changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  };

  const handleClose = () => {
    setShowError(false);
    setErrorMessage("");
    onClose();
  };

  return (
    <PopupModal isOpen={isOpen} onClose={handleClose}>
      <div className="change-password-modal">
        <div className="change-password-modal__header">
          <h2 className="popup-modal__header">Zmiana hasła</h2>
        </div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validation()}
        >
          {({ errors, values, handleSubmit, setFieldValue }) => (
            <Form>
              {showError && (
                <p className="change-password-modal__error">
                  {errorMessage}
                  <SVG type={SVG_ENUM.CLOSE_CIRCLE} />
                </p>
              )}

              <Field
                type="password"
                id="currentPassword"
                name="currentPassword"
                label="Aktualne hasło"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <Field
                type="password"
                id="newPassword"
                name="newPassword"
                label="Nowe hasło"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <Field
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                label="Powtórz nowe hasło"
                onFocus={() => setShowError(false)}
                as={Input}
              />

              <div className="popup-modal__buttons">
                <Button
                  label="Anuluj"
                  styleType="stroke"
                  onClick={handleClose}
                  type="button"
                />
                <Button
                  label="Zmień hasło"
                  className="button--primary"
                  styleType="primary"
                  onClick={() => handleSubmit()}
                  type="submit"
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PopupModal>
  );
};

export default ChangePasswordModal;
